// LanguageRegionSection.tsx — Settings → Language & Region.
//
// REAL controls:
//   Post timestamps → timestampStyle pref, consumed by formatPostTime in
//                     AuthorLink (feed cards). Comments stay relative — the
//                     comment payload carries no createdAt.
//   Time format     → clockFormat pref, consumed by formatAbsolute in
//                     AuthorLink + HeroPostCard. Visually gated behind
//                     Timestamps=Absolute the same way Edge light gates
//                     behind Sheen in Appearance — the hero line always shows
//                     an absolute time, so the pref still matters, but the
//                     gating keeps the mental model simple.
//
// DELIBERATELY DECORATIVE:
//   Language select — TruXSocial has no i18n layer yet; English is the only
//   real option. The select renders disabled with an honest hint rather than
//   pretending other languages exist. When i18n lands, wire it here.
import styled from 'styled-components';
import { Panel } from '@/ui';
import { usePreferences } from '@/hooks';
import type { TimestampStyleKey, ClockFormatKey } from '@/core/models/preferences';
import { RegionCard } from './RegionCard';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RowHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 44ch;
`;

const SegmentGroup = styled.div`
  display: flex;
  gap: 2px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  width: fit-content;
`;

const Segment = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  background: ${({ $active, theme }) => ($active ? theme.color.glassBorder : 'transparent')};
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $active, theme }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  transition: background 0.12s, color 0.12s;
  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: -2px;
  }
`;

/** Dims + disables the clock-format row while timestamps are relative. */
const Gated = styled.div<{ $on: boolean }>`
  transition: opacity 0.15s;
  opacity: ${({ $on }) => ($on ? 1 : 0.4)};
  pointer-events: ${({ $on }) => ($on ? 'auto' : 'none')};
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const TIMESTAMP_OPTIONS: { key: TimestampStyleKey; label: string }[] = [
  { key: 'relative', label: 'Relative' },
  { key: 'absolute', label: 'Absolute' },
];

const CLOCK_OPTIONS: { key: ClockFormatKey; label: string }[] = [
  { key: '12h', label: '12-hour' },
  { key: '24h', label: '24-hour' },
];

export function LanguageRegionSection() {
  const { prefs, setPreference } = usePreferences();
  const absolute = prefs.timestampStyle === 'absolute';

  return (
    <Stack>
      <RegionCard />

      <Card>
        <CardTitle>Language</CardTitle>
        <Row>
          <RowText>
            <RowLabel>Display language</RowLabel>
            <RowHint>English is the only available language for now.</RowHint>
          </RowText>
          <Select aria-label="Display language" value="en" disabled>
            <option value="en">English</option>
          </Select>
        </Row>
      </Card>

      <Card>
        <CardTitle>Formats</CardTitle>

        <Row>
          <RowText>
            <RowLabel>Post timestamps</RowLabel>
            <RowHint>
              Relative shows &quot;2h&quot;; absolute shows the full local date and time on
              feed posts.
            </RowHint>
          </RowText>
          <SegmentGroup role="group" aria-label="Post timestamps">
            {TIMESTAMP_OPTIONS.map(({ key, label }) => (
              <Segment
                key={key}
                type="button"
                $active={prefs.timestampStyle === key}
                aria-pressed={prefs.timestampStyle === key}
                onClick={() => void setPreference('timestampStyle', key)}
              >
                {label}
              </Segment>
            ))}
          </SegmentGroup>
        </Row>

        <Gated $on={absolute} aria-hidden={!absolute}>
          <Row>
            <RowText>
              <RowLabel>Time format</RowLabel>
              <RowHint>How absolute timestamps show the time of day.</RowHint>
            </RowText>
            <SegmentGroup role="group" aria-label="Time format">
              {CLOCK_OPTIONS.map(({ key, label }) => (
                <Segment
                  key={key}
                  type="button"
                  $active={prefs.clockFormat === key}
                  aria-pressed={prefs.clockFormat === key}
                  disabled={!absolute}
                  onClick={() => void setPreference('clockFormat', key)}
                >
                  {label}
                </Segment>
              ))}
            </SegmentGroup>
          </Row>
        </Gated>
      </Card>
    </Stack>
  );
}
